/**
 * @file Header.js
 * @description Componente de encabezado del dashboard
 * @module components/layout/Header
 * 
 * Funcionalidades:
 * - Muestra título y logo de la aplicación
 * - Botón de actualización manual de datos
 * - Indicador de última actualización en tiempo real
 * - Estados visuales de carga (isUpdating)
 * - Feedback visual para actualizaciones automáticas y manuales
 */

import React, { useState, useEffect } from 'react';
import { RotateCw } from 'lucide-react';

/**
 * Componente Header - Barra superior del dashboard
 * 
 * @component
 * @param {Function} props.onLogout - Callback para cerrar sesión
 * @param {Function} props.onRefresh - Callback para actualización manual
 * @param {boolean} props.isUpdating - Estado de actualización
 * @param {Date|null} props.lastUpdateTime - Timestamp de última actualización
 * @param {string} [props.secondaryActionLabel] - Texto del botón secundario
 * @param {Function} [props.onSecondaryAction] - Callback del botón secundario
 * @param {React.Component} [props.secondaryIcon] - Icono para el botón secundario
 */
const Header = ({
  onLogout,
  onRefresh,
  isUpdating,
  lastUpdateTime,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryIcon: SecondaryIcon,
}) => {
  const [displayTime, setDisplayTime] = useState('Cargando...');
  const [renderKey, setRenderKey] = useState(0);

  /**
   * Effect: Actualizar displayTime cuando cambia lastUpdateTime
   */
  useEffect(() => {
    if (lastUpdateTime) {
      try {
        const dateObj = typeof lastUpdateTime === 'number'
          ? new Date(lastUpdateTime)
          : lastUpdateTime;

        const timeStr = dateObj.toLocaleTimeString('es-MX', {
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });

        setDisplayTime(timeStr);
        setRenderKey(prev => prev + 1);
      } catch (error) {
        console.error('Error formateando fecha:', error);
        setDisplayTime('Error');
      }
    }
  }, [lastUpdateTime]);

  return (
    <div className="mb-6 sm:mb-8 lg:mb-10">
      {/* Mobile layout: título arriba-derecha, botones abajo-izquierda */}
      {/* Desktop layout: título izquierda, botones derecha (una sola fila) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">

        {/* Sección título + logo CDA */}
        {/* Mobile: título a la izquierda, logo CDA a su derecha, con pr para el hamburger */}
        {/* Desktop: mismo orden, logo después del título */}
        <div className="flex items-center gap-3 sm:gap-5 lg:gap-8 pr-10 sm:pr-0 min-w-0">
          <div className="shrink-0">
            <h1 className="text-2xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-0.5 sm:mb-2">Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-600">Inventario/Ubicación</p>
          </div>
          <img
            src="/logos/CDA2.png"
            alt="Grupo Continental"
            className="h-20 sm:h-28 lg:h-36 w-auto object-contain"
            style={{ mixBlendMode: 'multiply' }}
          />
        </div>

        {/* Sección controles: botones + última actualización */}
        {/* Mobile: alineado a la derecha. Desktop: alineado a la derecha */}
        <div className="flex flex-col items-end gap-1.5 sm:gap-2 shrink-0">
          <div className="flex items-center gap-2">
            {/* Botón de actualización manual */}
            <button
              onClick={onRefresh}
              className={`
                flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg
                text-sm sm:text-base
                transition-all duration-300
                ${isUpdating
                  ? 'bg-[#5a6d9c] cursor-wait'
                  : 'bg-[#1d2e61] hover:bg-[#002e5b]'
                }
                text-white
              `}
              disabled={isUpdating}
              title={isUpdating ? 'Actualizando...' : 'Actualizar datos'}
            >
              {isUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span className="hidden sm:inline">Actualizando...</span>
                </>
              ) : (
                <>
                  <RotateCw className="w-4 h-4" />
                  <span className="hidden sm:inline">Actualizar</span>
                </>
              )}
            </button>

            {/* Botón secundario (solo si se pasa por props) */}
            {secondaryActionLabel && onSecondaryAction && (
              <button
                onClick={onSecondaryAction}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-[#1d2e61] text-[#1d2e61] bg-white hover:bg-[#eef1f8] hover:border-[#002e5b] transition-colors"
              >
                {SecondaryIcon && <SecondaryIcon className="w-4 h-4" />}
                <span>{secondaryActionLabel}</span>
              </button>
            )}
          </div>

          {/* Indicador de última actualización */}
          <span
            className={`text-[11px] sm:text-xs transition-colors ${isUpdating ? 'text-[#1d2e61] font-semibold' : 'text-gray-500'
              }`}
            key={renderKey}
          >
            Última actualización: <strong>{displayTime}</strong>
          </span>
        </div>
      </div>
    </div>
  );
};

export default Header;